import React, { useState, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Chip,
  CircularProgress,
  Slide,
  InputAdornment,
  Collapse,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person,
  Email,
  Phone,
  LocationOn,
  Work,
  LinkedIn,
  Language,
  CheckCircle,
  ArrowBack,
  ArrowForward,
  Send,
  CloudUpload,
  Description as FileIcon,
  HomeOutlined,
  WarningAmberOutlined as WarnIcon,
  EmailOutlined as EmailOutIcon,
  PhoneAndroidOutlined as PhoneOutIcon,
  SecurityOutlined as SecurityIcon,
  CancelOutlined as BlockIcon,
  WorkOffOutlined as WorkOffIcon,
} from "@mui/icons-material";
import api from "../../services/api";
import PuestoDetalleModal from "./PuestoDetalleModal";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PASOS = ["Datos personales", "Experiencia", "Motivación"];

// Dominios de email permitidos (solo proveedores principales de México)
const DOMINIOS_PERMITIDOS = ["gmail.com", "hotmail.com", "icloud.com", "outlook.com"];

// Puestos de respaldo si la API no responde
const PUESTOS_FALLBACK = [
  "Fotógrafo / Camarógrafo",
  "Coordinador de Eventos",
  "Animador / Host",
  "Asistente de Producción",
  "Community Manager",
  "Diseñador Gráfico",
  "Otro",
];

const DISPONIBILIDAD = [
  { value: "inmediata", label: "Inmediata" },
  { value: "quincena",  label: "En 15 días" },
  { value: "mes",       label: "En 1 mes" },
  { value: "otro",      label: "Otro" },
];

const COMO_SE_ENTERO = [
  { value: "redes_sociales",  label: "Redes sociales" },
  { value: "recomendacion",   label: "Recomendación" },
  { value: "busqueda_web",    label: "Búsqueda en internet" },
  { value: "otro",            label: "Otro" },
];

const CIUDADES = [
  { value: "EDOMEX", label: "Estado de México (EDOMEX)" },
  { value: "CDMX",   label: "Ciudad de México (CDMX)" },
  { value: "CVA",    label: "Cuernavaca (CVA)" },
  { value: "HGO",    label: "Hidalgo (HGO)" },
];

const INITIAL_FORM = {
  nombre_completo: "",
  email: "",
  telefono: "",
  ciudad: "",
  direccion: "",
  fecha_nacimiento: "",
  puesto_interes: "",
  experiencia_anos: 0,
  descripcion_exp: "",
  habilidades: "",
  disponibilidad: "inmediata",
  linkedin_url: "",
  portfolio_url: "",
  mensaje: "",
  como_se_entero: "redes_sociales",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.04)",
    borderRadius: "12px",
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(0,201,255,0.4)" },
    "&.Mui-focused fieldset": { borderColor: "#00C9FF" },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#00C9FF" },
  "& .MuiInputBase-input": { color: "#fff" },
  "& .MuiSelect-icon": { color: "rgba(255,255,255,0.5)" },
};

const menuPaperSx = {
  PaperProps: {
    sx: {
      background: "#0f1630",
      border: "1px solid rgba(0,201,255,0.2)",
      "& .MuiMenuItem-root": { color: "rgba(255,255,255,0.8)" },
      "& .MuiMenuItem-root:hover": { background: "rgba(0,201,255,0.1)" },
      "& .MuiMenuItem-root.Mui-selected": { background: "rgba(0,201,255,0.15)" },
    },
  },
};

const PostulacionForm = ({ open, onClose }) => {
  const [paso, setPaso] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [exito, setExito] = useState(false);
  const [apiError, setApiError] = useState("");
  const [cv, setCv] = useState(null); // File object del CV
  const [primeraExp, setPrimeraExp] = useState(false); // Sin CV — primera experiencia laboral
  // Puestos activos desde la API (se carga al abrir el formulario)
  const [puestosActivos, setPuestosActivos] = useState([]);
  // Modal de detalle del puesto seleccionado
  const [puestoDetalle, setPuestoDetalle] = useState(null);
  const [openDetalle, setOpenDetalle] = useState(false);
  // Aviso de datos de contacto (se activa al enfocar email o teléfono)
  const [showContactTip, setShowContactTip] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetch("/api/vacantes")
      .then((r) => r.json())
      .then((data) => {
        const lista = data?.data;
        if (Array.isArray(lista) && lista.length > 0) {
          // Guardamos objetos completos para el modal de detalle
          setPuestosActivos(lista);
        } else {
          setPuestosActivos(PUESTOS_FALLBACK);
        }
      })
      .catch(() => setPuestosActivos(PUESTOS_FALLBACK));
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Teléfono: solo dígitos, máximo 10
    const processed = name === "telefono"
      ? value.replace(/\D/g, "").slice(0, 10)
      : value;
    setForm((p) => ({ ...p, [name]: processed }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
    setApiError("");

    // Al seleccionar un puesto, abrir el modal de detalle si tiene información
    if (name === "puesto_interes" && value) {
      const vacante = puestosActivos.find(
        (p) => typeof p === "object" && p.titulo === value
      );
      if (vacante && (vacante.descripcion || vacante.requisitos || vacante.beneficios)) {
        setPuestoDetalle(vacante);
        setOpenDetalle(true);
      } else {
        setPuestoDetalle(null);
      }
    }
  };

  const validarPaso = () => {
    const e = {};

    if (paso === 0) {
      // Nombre
      if (!form.nombre_completo.trim())
        e.nombre_completo = "Nombre requerido";

      // Email: formato + dominio permitido
      if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        e.email = "Ingresa un correo electrónico válido";
      } else {
        const dominio = form.email.split("@")[1]?.toLowerCase();
        if (!DOMINIOS_PERMITIDOS.includes(dominio)) {
          e.email = `Solo se aceptan correos de: ${DOMINIOS_PERMITIDOS.join(", ")}`;
        }
      }

      // Teléfono: exactamente 10 dígitos
      if (!form.telefono || form.telefono.length !== 10)
        e.telefono = "El teléfono debe tener exactamente 10 dígitos";

      // Ciudad
      if (!form.ciudad)
        e.ciudad = "Selecciona tu ciudad";

      // Dirección
      if (!form.direccion?.trim())
        e.direccion = "La dirección es requerida";

      // Fecha de nacimiento: requerida y mayor de 18 años
      if (!form.fecha_nacimiento) {
        e.fecha_nacimiento = "La fecha de nacimiento es requerida";
      } else {
        const hoy = new Date();
        const nac = new Date(form.fecha_nacimiento + "T00:00:00");
        let edad = hoy.getFullYear() - nac.getFullYear();
        const diffMes = hoy.getMonth() - nac.getMonth();
        if (diffMes < 0 || (diffMes === 0 && hoy.getDate() < nac.getDate())) edad--;
        if (edad < 18)
          e.fecha_nacimiento = "Debes ser mayor de 18 años para postularte";
      }
    }

    if (paso === 1) {
      if (!form.puesto_interes)
        e.puesto_interes = "Selecciona un puesto de interés";
    }

    if (paso === 2) {
      // CV solo requerido si el candidato NO marcó "primera experiencia laboral"
      if (!primeraExp) {
        if (!cv) {
          e.cv = 'El CV es requerido — adjunta tu currículum en formato PDF';
        } else {
          const allowed = ['application/pdf'];
          if (!allowed.includes(cv.type))
            e.cv = 'Solo se acepta formato PDF (.pdf)';
          else if (cv.size > 5 * 1024 * 1024)
            e.cv = 'El archivo no puede superar 5 MB';
        }
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const siguiente = () => {
    if (validarPaso()) setPaso((p) => p + 1);
  };

  const anterior = () => {
    setPaso((p) => p - 1);
    setApiError("");
  };

  const handleSubmit = async () => {
    if (!validarPaso()) return;
    setLoading(true);
    setApiError("");
    try {
      // Usar FormData para soportar subida de archivo
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') formData.append(k, v);
      });
      formData.append('primera_experiencia', primeraExp ? '1' : '0');
      if (cv) formData.append('cv', cv);

      const res = await api.post('/postulaciones', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // El interceptor puede o no desempacar; revisamos ambas formas
      const data = res.data;
      if (data?.success || data?.id) {
        setExito(true);
      } else {
        setApiError(data?.error || 'Error al enviar. Intenta de nuevo.');
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al conectar con el servidor.';
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCvChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      setErrors((p) => ({ ...p, cv: 'Solo se aceptan PDF o Word (.doc, .docx)' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((p) => ({ ...p, cv: 'El archivo no puede superar 5 MB' }));
      return;
    }
    setCv(file);
    setErrors((p) => ({ ...p, cv: '' }));
    // Limpiar el input para permitir seleccionar el mismo archivo de nuevo
    e.target.value = '';
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
    setTimeout(() => {
      setPaso(0);
      setForm(INITIAL_FORM);
      setErrors({});
      setExito(false);
      setApiError("");
      setCv(null);
      setPrimeraExp(false);
      setPuestoDetalle(null);
      setOpenDetalle(false);
      setShowContactTip(false);
    }, 300);
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: "transparent",
          boxShadow: "none",
          overflow: "visible",
          m: { xs: 1, sm: 3 },
        },
      }}
      slotProps={{
        backdrop: {
          sx: { background: "rgba(4,6,20,0.92)", backdropFilter: "blur(14px)" },
        },
      }}
    >
      <style>{`
        @keyframes gradientFlow {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .form-section { animation: fadeUp 0.35s ease forwards; }
      `}</style>

      <Box
        sx={{
          position: "relative",
          borderRadius: "24px",
          overflow: "hidden",
          background: "linear-gradient(145deg, #090d1f 0%, #0f1630 60%, #0a1020 100%)",
          border: "1px solid rgba(0,201,255,0.18)",
          boxShadow:
            "0 0 0 1px rgba(146,254,157,0.06), 0 32px 80px rgba(0,0,0,0.75), 0 0 60px rgba(0,201,255,0.1)",
        }}
      >
        {/* Barra superior animada */}
        <Box
          sx={{
            height: 5,
            background: "linear-gradient(90deg, #00C9FF, #92FE9D, #8250ff, #00C9FF)",
            backgroundSize: "300% 100%",
            animation: "gradientFlow 4s linear infinite",
          }}
        />

        {/* Botón cerrar */}
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            zIndex: 10,
            color: "rgba(255,255,255,0.4)",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.1)",
            "&:hover": { color: "#fff", background: "rgba(255,255,255,0.1)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={{ p: { xs: 3, sm: 4 }, pt: { xs: 2.5, sm: 3 } }}>

          {/* ── Header ── */}
          {!exito && (
            <>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Chip
                  label="ÚNETE AL EQUIPO"
                  size="small"
                  sx={{
                    mb: 1.5,
                    background: "rgba(0,201,255,0.1)",
                    border: "1px solid rgba(0,201,255,0.3)",
                    color: "#00C9FF",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    fontSize: "0.6rem",
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    background: "linear-gradient(135deg, #00C9FF, #92FE9D)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Postúlate a A La Vuelta 360°
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", mt: 0.5, display: "block" }}>
                  Completa el formulario · Te contactaremos en 3-5 días hábiles
                </Typography>
              </Box>

              {/* Stepper */}
              <Stepper activeStep={paso} alternativeLabel sx={{ mb: 3 }}>
                {PASOS.map((label, i) => (
                  <Step key={label}>
                    <StepLabel
                      sx={{
                        "& .MuiStepLabel-label": {
                          color: i <= paso ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.3)",
                          fontSize: "0.72rem",
                          fontWeight: i === paso ? 700 : 400,
                        },
                        "& .MuiStepIcon-root": {
                          color: i < paso ? "#92FE9D" : i === paso ? "#00C9FF" : "rgba(255,255,255,0.15)",
                        },
                        "& .MuiStepIcon-root.Mui-active": { color: "#00C9FF" },
                        "& .MuiStepIcon-root.Mui-completed": { color: "#92FE9D" },
                        "& .MuiStepConnector-line": { borderColor: "rgba(255,255,255,0.15)" },
                      }}
                    />
                  </Step>
                ))}
              </Stepper>
            </>
          )}

          {/* ── ÉXITO ── */}
          {exito && (
            <Box className="form-section" sx={{ textAlign: "center", py: 4 }}>
              <Box sx={{ fontSize: 64, mb: 2 }}>🎉</Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  mb: 1.5,
                  background: "linear-gradient(135deg, #00C9FF, #92FE9D)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                ¡Postulación enviada!
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mb: 1, lineHeight: 1.7 }}>
                Recibimos tu solicitud correctamente.
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", lineHeight: 1.7 }}>
                Revisa tu correo{" "}
                <Box component="span" sx={{ color: "#00C9FF", fontWeight: 600 }}>
                  {form.email}
                </Box>{" "}
                — te enviamos una confirmación. 📩
              </Typography>
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  background: "rgba(146,254,157,0.06)",
                  border: "1px solid rgba(146,254,157,0.2)",
                  borderRadius: "14px",
                }}
              >
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
                  Si eres seleccionado(a), recibirás un correo con tus credenciales de acceso al portal.
                </Typography>
              </Box>
              <Button
                fullWidth
                onClick={handleClose}
                sx={{
                  mt: 3,
                  py: 1.4,
                  borderRadius: "12px",
                  background: "linear-gradient(90deg,#00C9FF,#92FE9D)",
                  color: "#060d1a",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { background: "linear-gradient(90deg,#92FE9D,#00C9FF)" },
                }}
              >
                <CheckCircle sx={{ mr: 1, fontSize: 18 }} />
                ¡Entendido, gracias!
              </Button>
            </Box>
          )}

          {/* ── PASO 0: Datos personales ── */}
          {!exito && paso === 0 && (
            <Box className="form-section" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                fullWidth label="Nombre completo *" name="nombre_completo"
                value={form.nombre_completo} onChange={handleChange}
                error={!!errors.nombre_completo} helperText={errors.nombre_completo}
                sx={fieldSx}
                InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ color: "rgba(255,255,255,0.3)", fontSize: 18 }} /></InputAdornment> }}
              />
              <TextField
                fullWidth label="Correo electrónico *" name="email" type="email"
                value={form.email} onChange={handleChange}
                error={!!errors.email} helperText={errors.email}
                onFocus={() => setShowContactTip(true)}
                sx={fieldSx}
                InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ color: "rgba(255,255,255,0.3)", fontSize: 18 }} /></InputAdornment> }}
              />
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                <TextField
                  fullWidth label="Teléfono *" name="telefono"
                  value={form.telefono} onChange={handleChange}
                  error={!!errors.telefono} helperText={errors.telefono}
                  sx={fieldSx}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ color: "rgba(255,255,255,0.3)", fontSize: 18 }} /></InputAdornment> }}
                />
                <TextField
                  select fullWidth label="Ciudad *" name="ciudad"
                  value={form.ciudad} onChange={handleChange}
                  error={!!errors.ciudad} helperText={errors.ciudad}
                  sx={fieldSx} SelectProps={{ MenuProps: menuPaperSx }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><LocationOn sx={{ color: "rgba(255,255,255,0.3)", fontSize: 18 }} /></InputAdornment> }}
                >
                  {CIUDADES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </TextField>
              </Box>
              <TextField
                fullWidth label="Dirección *" name="direccion"
                value={form.direccion} onChange={handleChange}
                error={!!errors.direccion} helperText={errors.direccion}
                placeholder="Calle, número, colonia..."
                sx={fieldSx}
                InputProps={{ startAdornment: <InputAdornment position="start"><HomeOutlined sx={{ color: "rgba(255,255,255,0.3)", fontSize: 18 }} /></InputAdornment> }}
              />

              {/* ── Aviso de datos de contacto ── */}
              <Collapse in={showContactTip} unmountOnExit>
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: "16px",
                    overflow: "hidden",
                    background: "linear-gradient(135deg, rgba(255,167,38,0.07) 0%, rgba(0,201,255,0.05) 100%)",
                    border: "1px solid rgba(255,167,38,0.3)",
                    p: 2,
                  }}
                >
                  {/* Botón cerrar */}
                  <IconButton
                    size="small"
                    onClick={() => setShowContactTip(false)}
                    sx={{
                      position: "absolute", top: 8, right: 8,
                      color: "rgba(255,255,255,0.35)",
                      background: "rgba(255,255,255,0.05)",
                      "&:hover": { color: "#fff", background: "rgba(255,255,255,0.1)" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>

                  {/* Encabezado */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2, pr: 3 }}>
                    <WarnIcon sx={{ color: "#ffa726", fontSize: 18, flexShrink: 0 }} />
                    <Typography sx={{ fontWeight: 800, color: "#ffa726", fontSize: "0.78rem", letterSpacing: "0.06em" }}>
                      DATOS DE CONTACTO — INFORMACIÓN CLAVE
                    </Typography>
                  </Box>

                  {/* Mensaje principal */}
                  <Typography sx={{ fontSize: "0.83rem", color: "rgba(255,255,255,0.72)", lineHeight: 1.65, mb: 1.5 }}>
                    Ingresa un correo y número de teléfono{" "}
                    <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>reales y activos</Box>.
                    {" "}Todo el proceso de reclutamiento se coordinará exclusivamente por estos medios:
                  </Typography>

                  {/* Ítems */}
                  {[
                    { icon: <EmailOutIcon sx={{ fontSize: 15 }} />, color: "#00C9FF",  text: "Correo electrónico — confirmación de postulación y actualizaciones del proceso" },
                    { icon: <PhoneOutIcon sx={{ fontSize: 15 }} />, color: "#92FE9D",  text: "Teléfono — nuestro equipo te contactará por WhatsApp o llamada directa" },
                    { icon: <SecurityIcon  sx={{ fontSize: 15 }} />, color: "#b39ddb", text: "Nunca te pediremos contraseñas, pagos ni datos bancarios por ningún canal" },
                  ].map(({ icon, color, text }, i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.8 }}>
                      <Box sx={{ color, flexShrink: 0, mt: 0.2 }}>{icon}</Box>
                      <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.68)", lineHeight: 1.55 }}>
                        {text}
                      </Typography>
                    </Box>
                  ))}

                  {/* Advertencia */}
                  <Box
                    sx={{
                      mt: 1.5,
                      display: "flex", alignItems: "flex-start", gap: 1,
                      background: "rgba(211,47,47,0.1)",
                      border: "1px solid rgba(211,47,47,0.25)",
                      borderRadius: "10px",
                      px: 1.5, py: 1,
                    }}
                  >
                    <BlockIcon sx={{ color: "rgba(244,67,54,0.85)", fontSize: 15, mt: 0.15, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "0.76rem", color: "rgba(244,67,54,0.85)", fontWeight: 600, lineHeight: 1.5 }}>
                      Proporcionar datos incorrectos o falsos resultará en la{" "}
                      <Box component="span" sx={{ textDecoration: "underline" }}>exclusión automática e inmediata</Box>{" "}
                      del proceso de selección.
                    </Typography>
                  </Box>
                </Box>
              </Collapse>

              <TextField
                fullWidth label="Fecha de nacimiento *" name="fecha_nacimiento" type="date"
                value={form.fecha_nacimiento} onChange={handleChange}
                error={!!errors.fecha_nacimiento} helperText={errors.fecha_nacimiento}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().split("T")[0]; })() }}
                sx={fieldSx}
              />
            </Box>
          )}

          {/* ── PASO 1: Experiencia ── */}
          {!exito && paso === 1 && (
            <Box className="form-section" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                select fullWidth label="Puesto de interés *" name="puesto_interes"
                value={form.puesto_interes} onChange={handleChange}
                error={!!errors.puesto_interes} helperText={errors.puesto_interes}
                sx={fieldSx} SelectProps={{ MenuProps: menuPaperSx }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Work sx={{ color: "rgba(255,255,255,0.3)", fontSize: 18 }} /></InputAdornment> }}
              >
                {puestosActivos.map((p) => {
                  const titulo = typeof p === "object" ? p.titulo : p;
                  return <MenuItem key={titulo} value={titulo}>{titulo}</MenuItem>;
                })}
              </TextField>

              {/* Chip para (re)abrir el modal de detalle del puesto */}
              {puestoDetalle && (
                <Box sx={{ mt: -0.5 }}>
                  <Chip
                    label="📋 Ver descripción del puesto →"
                    size="small"
                    onClick={() => setOpenDetalle(true)}
                    sx={{
                      cursor: "pointer",
                      background: "rgba(0,201,255,0.08)",
                      border: "1px solid rgba(0,201,255,0.25)",
                      color: "#00C9FF",
                      fontSize: "0.74rem",
                      fontWeight: 600,
                      "&:hover": {
                        background: "rgba(0,201,255,0.15)",
                        borderColor: "rgba(0,201,255,0.45)",
                      },
                    }}
                  />
                </Box>
              )}

              <TextField
                select fullWidth label="Años de experiencia" name="experiencia_anos"
                value={form.experiencia_anos} onChange={handleChange}
                sx={fieldSx} SelectProps={{ MenuProps: menuPaperSx }}
              >
                {[{v:0,l:"Sin experiencia previa"},{v:1,l:"1 año"},{v:2,l:"2 años"},{v:3,l:"3 años"},{v:5,l:"5+ años"}].map(o =>
                  <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
              </TextField>
              <TextField
                fullWidth multiline rows={3}
                label="Describe tu experiencia" name="descripcion_exp"
                value={form.descripcion_exp} onChange={handleChange}
                placeholder="¿En qué eventos o proyectos has trabajado?"
                sx={fieldSx}
              />
              <TextField
                fullWidth label="Habilidades clave" name="habilidades"
                value={form.habilidades} onChange={handleChange}
                placeholder="Ej: Fotografía, Edición, Trato al cliente..."
                sx={fieldSx}
              />
              <TextField
                select fullWidth label="Disponibilidad para iniciar" name="disponibilidad"
                value={form.disponibilidad} onChange={handleChange}
                sx={fieldSx} SelectProps={{ MenuProps: menuPaperSx }}
              >
                {DISPONIBILIDAD.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Box>
          )}

          {/* ── PASO 2: Motivación ── */}
          {!exito && paso === 2 && (
            <Box className="form-section" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                fullWidth multiline rows={4}
                label="¿Por qué quieres unirte a A La Vuelta 360°? *"
                name="mensaje" value={form.mensaje} onChange={handleChange}
                placeholder="Cuéntanos qué te motiva, qué puedes aportar y por qué eres el candidato ideal..."
                sx={fieldSx}
              />
              <TextField
                fullWidth label="LinkedIn (opcional)" name="linkedin_url"
                value={form.linkedin_url} onChange={handleChange}
                placeholder="https://linkedin.com/in/tu-perfil"
                sx={fieldSx}
                InputProps={{ startAdornment: <InputAdornment position="start"><LinkedIn sx={{ color: "rgba(255,255,255,0.3)", fontSize: 18 }} /></InputAdornment> }}
              />
              <TextField
                fullWidth label="Portafolio / Sitio web (opcional)" name="portfolio_url"
                value={form.portfolio_url} onChange={handleChange}
                placeholder="https://tuportafolio.com"
                sx={fieldSx}
                InputProps={{ startAdornment: <InputAdornment position="start"><Language sx={{ color: "rgba(255,255,255,0.3)", fontSize: 18 }} /></InputAdornment> }}
              />
              <TextField
                select fullWidth label="¿Cómo te enteraste de nosotros?" name="como_se_entero"
                value={form.como_se_entero} onChange={handleChange}
                sx={fieldSx} SelectProps={{ MenuProps: menuPaperSx }}
              >
                {COMO_SE_ENTERO.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>

              {/* ── Checkbox: primera experiencia laboral ── */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: '14px',
                  background: primeraExp
                    ? 'rgba(130,80,255,0.08)'
                    : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${primeraExp ? 'rgba(130,80,255,0.35)' : 'rgba(255,255,255,0.08)'}`,
                  transition: 'all 0.25s',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={primeraExp}
                      onChange={(e) => {
                        setPrimeraExp(e.target.checked);
                        // Si marca primera exp, quitar CV adjunto y limpiar error
                        if (e.target.checked) {
                          setCv(null);
                          setErrors((p) => ({ ...p, cv: '' }));
                        }
                      }}
                      sx={{
                        color: 'rgba(130,80,255,0.5)',
                        '&.Mui-checked': { color: '#8250ff' },
                        p: 0.75,
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ color: primeraExp ? '#c4a8ff' : 'rgba(255,255,255,0.7)', fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.3 }}>
                        Esta es mi primera experiencia laboral
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.73rem', mt: 0.2 }}>
                        No cuento con CV — es mi primera vez en el mundo laboral
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, alignItems: 'flex-start', gap: 0.5 }}
                />

                {/* Banner informativo si activa primera experiencia */}
                <Collapse in={primeraExp} unmountOnExit>
                  <Box
                    sx={{
                      mt: 1.5,
                      display: 'flex', alignItems: 'flex-start', gap: 1.2,
                      background: 'rgba(130,80,255,0.1)',
                      border: '1px solid rgba(130,80,255,0.25)',
                      borderRadius: '10px',
                      px: 1.5, py: 1.2,
                    }}
                  >
                    <WorkOffIcon sx={{ color: '#b39ddb', fontSize: 18, mt: 0.1, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>
                      ¡Sin problema! Valoramos el entusiasmo y las ganas de aprender. Puedes{' '}
                      <Box component="span" sx={{ color: '#c4a8ff', fontWeight: 700 }}>prescindir del CV</Box>
                      {' '}— tu carta de motivación es lo más importante.
                    </Typography>
                  </Box>
                </Collapse>
              </Box>

              {/* ── Campo CV (oculto si es primera experiencia) ── */}
              <Collapse in={!primeraExp} unmountOnExit>
                <Box>
                  <input
                    id="cv-upload"
                    type="file"
                    accept=".pdf,application/pdf"
                    style={{ display: 'none' }}
                    onChange={handleCvChange}
                  />
                  <label htmlFor="cv-upload">
                    <Box
                      sx={{
                        border: `2px dashed ${errors.cv ? 'rgba(211,47,47,0.6)' : cv ? 'rgba(146,254,157,0.4)' : 'rgba(0,201,255,0.25)'}`,
                        borderRadius: '14px',
                        p: 2.5,
                        cursor: 'pointer',
                        textAlign: 'center',
                        background: cv ? 'rgba(146,254,157,0.04)' : 'rgba(0,201,255,0.02)',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: cv ? 'rgba(146,254,157,0.7)' : '#00C9FF',
                          background: cv ? 'rgba(146,254,157,0.07)' : 'rgba(0,201,255,0.06)',
                        },
                      }}
                    >
                      {cv ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <FileIcon sx={{ color: '#92FE9D', fontSize: 28, flexShrink: 0 }} />
                          <Box sx={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ color: '#92FE9D', fontWeight: 600,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {cv.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                              {(cv.size / 1024 / 1024).toFixed(2)} MB · Haz clic para cambiar
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.preventDefault(); setCv(null); }}
                            sx={{ color: 'rgba(255,255,255,0.35)', '&:hover': { color: '#ff6b6b' } }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box>
                          <CloudUpload sx={{ color: 'rgba(0,201,255,0.55)', fontSize: 36, mb: 0.5 }} />
                          <Typography variant="body2"
                            sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600, mb: 0.3 }}>
                            Subir CV / Currículum{' '}
                            <Box component="span" sx={{ color: '#ff6b6b', fontWeight: 700 }}>*</Box>
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                            Solo PDF · Máximo 5 MB
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </label>
                  {errors.cv && (
                    <Typography variant="caption"
                      sx={{ color: '#f44336', display: 'block', mt: 0.5, ml: 1.5, fontSize: '0.72rem' }}>
                      {errors.cv}
                    </Typography>
                  )}
                </Box>
              </Collapse>

              {apiError && (
                <Alert
                  severity="error"
                  sx={{
                    background: "rgba(211,47,47,0.12)",
                    border: "1px solid rgba(211,47,47,0.3)",
                    color: "#ff6b6b",
                    borderRadius: "12px",
                    "& .MuiAlert-icon": { color: "#ff6b6b" },
                  }}
                >
                  {apiError}
                </Alert>
              )}
            </Box>
          )}

          {/* ── Navegación ── */}
          {!exito && (
            <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
              {paso > 0 && (
                <Button
                  onClick={anterior}
                  disabled={loading}
                  startIcon={<ArrowBack />}
                  sx={{
                    flex: "0 0 auto",
                    px: 2.5, py: 1.4,
                    borderRadius: "12px",
                    color: "rgba(255,255,255,0.6)",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    textTransform: "none",
                    "&:hover": { background: "rgba(255,255,255,0.09)" },
                  }}
                >
                  Atrás
                </Button>
              )}

              {paso < PASOS.length - 1 ? (
                <Button
                  fullWidth onClick={siguiente}
                  endIcon={<ArrowForward />}
                  sx={{
                    py: 1.4, borderRadius: "12px",
                    background: "linear-gradient(90deg,#00C9FF,#92FE9D)",
                    color: "#060d1a", fontWeight: 700, textTransform: "none",
                    "&:hover": { background: "linear-gradient(90deg,#0098CC,#6FCA7D)" },
                  }}
                >
                  Siguiente
                </Button>
              ) : (
                <Button
                  fullWidth onClick={handleSubmit}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} sx={{ color: "#060d1a" }} /> : <Send />}
                  sx={{
                    py: 1.4, borderRadius: "12px",
                    background: loading
                      ? "rgba(0,201,255,0.4)"
                      : "linear-gradient(90deg,#00C9FF,#92FE9D)",
                    color: "#060d1a", fontWeight: 700, textTransform: "none",
                    transition: "all 0.3s",
                    "&:hover:not(:disabled)": {
                      background: "linear-gradient(90deg,#92FE9D,#00C9FF)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {loading ? "Enviando..." : "Enviar postulación 🚀"}
                </Button>
              )}
            </Box>
          )}

          {/* Nota de privacidad */}
          {!exito && (
            <Typography variant="caption" sx={{ display: "block", textAlign: "center", color: "rgba(255,255,255,0.25)", mt: 2, fontSize: "0.68rem" }}>
              🔒 Tu información está protegida y solo se usará para el proceso de selección.
            </Typography>
          )}
        </Box>
      </Box>
    </Dialog>

    {/* Modal de detalle del puesto seleccionado */}
    <PuestoDetalleModal
      open={openDetalle}
      onClose={() => setOpenDetalle(false)}
      vacante={puestoDetalle}
    />
    </>
  );
};

export default PostulacionForm;
